import { useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Copy, Share2, ExternalLink, ShieldCheck, Printer, FileDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Candidate-facing card showing the two permanent Report IDs, a QR code
 * pointing at the public verification URL, and copy/share/print helpers.
 *
 * Rendered on the Candidate Dashboard once `case_status === "report_generated"`.
 */
export default function CredentialCard({ candidate }) {
  const ipcbId = candidate?.ipcb_report_id || null;
  const instId = candidate?.institution_report_id || null;
  const [activeTab, setActiveTab] = useState(ipcbId ? "ipcb" : "inst");

  const verifyBase = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin.replace(/\/$/, "");
  }, []);

  const activeReportId = activeTab === "ipcb" ? ipcbId : instId;
  const activeLabel =
    activeTab === "ipcb" ? "IPCB Evaluation Report" : "Recognition of Prior Learning Competency Report (RPL)";
  const verifyUrl = activeReportId ? `${verifyBase}/verify/${activeReportId}` : "";

  if (!ipcbId && !instId) return null;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const share = async () => {
    if (!verifyUrl) return;
    const shareData = {
      title: "Credentia AI - Credential Verification",
      text: `Verify my RPL credential (${activeReportId})`,
      url: verifyUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(verifyUrl);
        toast.success("Verification link copied to clipboard");
      }
    } catch {
      /* user cancelled — ignore */
    }
  };

  const print = () => window.print();

  const downloadPdf = async () => {
    if (!candidate?.candidate_id) return;
    try {
      toast.info("Generating your official PDF…");
      const r = await axios.get(`${API}/candidates/${candidate.candidate_id}/report.pdf`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 60000,
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (candidate.full_name || candidate.candidate_id).replace(/[^\w\-]+/g, "_");
      a.download = `${safe}_RPL_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Official PDF downloaded.");
    } catch (e) {
      // Server-side PDF temporarily unavailable → graceful browser fallback.
      if (e?.response?.status === 503) {
        toast.info(
          "Opening print dialog. Choose 'Save as PDF' as the destination for the best quality.",
          { duration: 6000 }
        );
        setTimeout(() => {
          try { window.print(); } catch (_) {
            toast.error("Press Ctrl+P or ⌘+P to save as PDF.");
          }
        }, 800);
      } else {
        toast.error("Could not download PDF. Please try again or contact your assessor.");
      }
    }
  };

  return (
    <Card
      className="border-blue-200 bg-gradient-to-br from-slate-50 via-white to-blue-50"
      data-testid="credential-card"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <ShieldCheck className="w-5 h-5 text-blue-700" />
          Your Verifiable Credential
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Share either Report ID — or the QR code below — with any employer or regulator to instantly verify your credential. No login required for them.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Tabs */}
        {ipcbId && instId && (
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="credential-tabs">
            <button
              data-testid="credential-tab-ipcb"
              onClick={() => setActiveTab("ipcb")}
              className={`px-4 py-1.5 text-sm rounded-md transition ${
                activeTab === "ipcb"
                  ? "bg-blue-700 text-white shadow"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              IPCB Report
            </button>
            <button
              data-testid="credential-tab-inst"
              onClick={() => setActiveTab("inst")}
              className={`px-4 py-1.5 text-sm rounded-md transition ${
                activeTab === "inst"
                  ? "bg-blue-700 text-white shadow"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              RPL Report
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
          {/* Left: details */}
          <div className="space-y-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                {activeLabel}
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="font-mono text-base sm:text-lg font-bold text-slate-900 bg-white border border-slate-200 rounded px-3 py-1.5 tracking-wider"
                  data-testid="credential-report-id"
                >
                  {activeReportId}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(activeReportId, "Report ID")}
                  data-testid="credential-copy-id-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy ID
                </Button>
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                Public Verification Link
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-700 hover:underline break-all"
                  data-testid="credential-verify-link"
                >
                  {verifyUrl}
                </a>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(verifyUrl, "Verification link")}
                  data-testid="credential-copy-link-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={share}
                  data-testid="credential-share-btn"
                >
                  <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(verifyUrl, "_blank", "noopener")}
                  data-testid="credential-open-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                </Button>
                <Button
                  size="sm"
                  onClick={downloadPdf}
                  data-testid="credential-download-pdf-btn"
                  className="bg-blue-700 hover:bg-blue-800 text-white"
                >
                  <FileDown className="w-3.5 h-3.5 mr-1" /> Download official PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={print}
                  data-testid="credential-print-btn"
                >
                  <Printer className="w-3.5 h-3.5 mr-1" /> Print this card
                </Button>
              </div>
            </div>

            {ipcbId && instId && (
              <p className="text-xs text-slate-500 leading-relaxed pt-1">
                Both report IDs verify to the same credential record — IPCB
                certifies the assessment, the Institution issues the award.
                Employers may scan either.
              </p>
            )}
          </div>

          {/* Right: QR code */}
          <div
            className="bg-white rounded-lg border border-slate-200 p-3 flex flex-col items-center"
            data-testid="credential-qr-block"
          >
            {verifyUrl ? (
              <QRCodeCanvas
                value={verifyUrl}
                size={160}
                level="M"
                includeMargin={false}
              />
            ) : null}
            <p className="text-[11px] text-slate-500 mt-2 text-center max-w-[160px]">
              Scan with any phone camera to verify
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
